/**
 * Order Constants
 *
 * Centralized constants for order statuses, sources, and categories.
 * This file provides a single source of truth for all order-related enum values.
 *
 * Usage:
 * - Use ORDER_STATUS constants instead of hardcoded strings
 * - Use ORDER_STATUS_OPTIONS for dropdowns/selects
 * - TypeScript will provide autocomplete and type safety
 */

import type { OrderStatus, OrderSource, OrderCategory } from "@/types/order";

// ============================================================================
// Order Status Constants
// ============================================================================

/**
 * Order status constant values
 * Use these instead of hardcoded strings for type safety
 * NOTE: Processing status was removed from workflow - orders go directly New → Ready
 */
export const ORDER_STATUS = {
  NEW: 'New',
  AWAITING_PAYMENT: 'Awaiting Payment',
  COMPLETED: 'Completed',
  OUT_FOR_DELIVERY: 'Out for Delivery',
  DELIVERED: 'Delivered',
  DELIVERY_FAILED: 'Delivery Failed',
  CANCELLED: 'Cancelled',
} as const;

/**
 * Array of all order statuses for UI (dropdowns, filters)
 * NOTE: Processing removed from workflow
 */
export const ORDER_STATUS_OPTIONS: readonly OrderStatus[] = [
  ORDER_STATUS.NEW,
  ORDER_STATUS.AWAITING_PAYMENT,
  ORDER_STATUS.COMPLETED,
  ORDER_STATUS.OUT_FOR_DELIVERY,
  ORDER_STATUS.DELIVERED,
  ORDER_STATUS.DELIVERY_FAILED,
  ORDER_STATUS.CANCELLED,
] as const;

/**
 * User-friendly labels for order statuses
 * Note: Using string keys for flexibility with legacy "Processing" orders
 */
export const ORDER_STATUS_LABELS: Record<string, string> = {
  [ORDER_STATUS.NEW]: 'New',
  [ORDER_STATUS.AWAITING_PAYMENT]: 'Awaiting Payment',
  'Processing': 'Processing', // Legacy - kept for backward compatibility
  [ORDER_STATUS.COMPLETED]: 'Completed',
  [ORDER_STATUS.OUT_FOR_DELIVERY]: 'Out for Delivery',
  [ORDER_STATUS.DELIVERED]: 'Delivered',
  [ORDER_STATUS.DELIVERY_FAILED]: 'Delivery Failed',
  [ORDER_STATUS.CANCELLED]: 'Cancelled',
};

// ============================================================================
// Order Source Constants
// ============================================================================

/**
 * Order source constant values
 * Represents where the order originated from
 */
export const ORDER_SOURCE = {
  VIBER: 'Viber',
  WHATSAPP: 'WhatsApp',
  TELEGRAM: 'Telegram',
  MANUAL: 'Manual',
  FACEBOOK_MESSENGER: 'Facebook Messenger',
  INSTAGRAM: 'Instagram',
  EXTERNAL: 'External',
} as const;

/**
 * Array of all order sources
 * Useful for dropdowns, validation, and iteration
 */
export const ORDER_SOURCE_OPTIONS: readonly OrderSource[] = [
  ORDER_SOURCE.VIBER,
  ORDER_SOURCE.WHATSAPP,
  ORDER_SOURCE.TELEGRAM,
  ORDER_SOURCE.MANUAL,
  ORDER_SOURCE.FACEBOOK_MESSENGER,
  ORDER_SOURCE.INSTAGRAM,
  ORDER_SOURCE.EXTERNAL,
] as const;

/**
 * User-friendly labels for order sources
 */
export const ORDER_SOURCE_LABELS: Record<OrderSource, string> = {
  [ORDER_SOURCE.VIBER]: 'Viber',
  [ORDER_SOURCE.WHATSAPP]: 'WhatsApp',
  [ORDER_SOURCE.TELEGRAM]: 'Telegram',
  [ORDER_SOURCE.MANUAL]: 'Manual Entry',
  [ORDER_SOURCE.FACEBOOK_MESSENGER]: 'Facebook Messenger',
  [ORDER_SOURCE.INSTAGRAM]: 'Instagram',
  [ORDER_SOURCE.EXTERNAL]: 'External',
};

// ============================================================================
// Order Category Constants
// ============================================================================

/**
 * Order category constant values
 * Product/service categories for classification
 */
export const ORDER_CATEGORY = {
  CLEANING_SUPPLIES: 'Cleaning Supplies',
  KITCHEN_ITEMS: 'Kitchen Items',
  BATHROOM_PRODUCTS: 'Bathroom Products',
  HOME_DECOR: 'Home Decor',
  STORAGE_ORGANIZATION: 'Storage & Organization',
  TEXTILES: 'Textiles',
  SMALL_APPLIANCES: 'Small Appliances',
  TOOLS_HARDWARE: 'Tools & Hardware',
  HOUSEHOLD_GOODS: 'Household Goods',
  CLOTHING_FASHION: 'Clothing & Fashion',
  ELECTRONICS: 'Electronics',
  FOOD_RESTAURANT: 'Food & Restaurant',
  PHARMACY_MEDICAL: 'Pharmacy & Medical',
  RETAIL_GENERAL: 'Retail & General',
  OTHER: 'Other',
} as const;

/**
 * Array of all order categories
 * Useful for dropdowns, validation, and iteration
 */
export const ORDER_CATEGORY_OPTIONS: readonly OrderCategory[] = [
  ORDER_CATEGORY.CLEANING_SUPPLIES,
  ORDER_CATEGORY.KITCHEN_ITEMS,
  ORDER_CATEGORY.BATHROOM_PRODUCTS,
  ORDER_CATEGORY.HOME_DECOR,
  ORDER_CATEGORY.STORAGE_ORGANIZATION,
  ORDER_CATEGORY.TEXTILES,
  ORDER_CATEGORY.SMALL_APPLIANCES,
  ORDER_CATEGORY.TOOLS_HARDWARE,
  ORDER_CATEGORY.HOUSEHOLD_GOODS,
  ORDER_CATEGORY.CLOTHING_FASHION,
  ORDER_CATEGORY.ELECTRONICS,
  ORDER_CATEGORY.FOOD_RESTAURANT,
  ORDER_CATEGORY.PHARMACY_MEDICAL,
  ORDER_CATEGORY.RETAIL_GENERAL,
  ORDER_CATEGORY.OTHER,
] as const;

/**
 * User-friendly labels for order categories
 */
export const ORDER_CATEGORY_LABELS: Record<OrderCategory, string> = {
  [ORDER_CATEGORY.CLEANING_SUPPLIES]: 'Cleaning Supplies',
  [ORDER_CATEGORY.KITCHEN_ITEMS]: 'Kitchen Items',
  [ORDER_CATEGORY.BATHROOM_PRODUCTS]: 'Bathroom Products',
  [ORDER_CATEGORY.HOME_DECOR]: 'Home Decor',
  [ORDER_CATEGORY.STORAGE_ORGANIZATION]: 'Storage & Organization',
  [ORDER_CATEGORY.TEXTILES]: 'Textiles',
  [ORDER_CATEGORY.SMALL_APPLIANCES]: 'Small Appliances',
  [ORDER_CATEGORY.TOOLS_HARDWARE]: 'Tools & Hardware',
  [ORDER_CATEGORY.HOUSEHOLD_GOODS]: 'Household Goods',
  [ORDER_CATEGORY.CLOTHING_FASHION]: 'Clothing & Fashion',
  [ORDER_CATEGORY.ELECTRONICS]: 'Electronics',
  [ORDER_CATEGORY.FOOD_RESTAURANT]: 'Food & Restaurant',
  [ORDER_CATEGORY.PHARMACY_MEDICAL]: 'Pharmacy & Medical',
  [ORDER_CATEGORY.RETAIL_GENERAL]: 'Retail & General',
  [ORDER_CATEGORY.OTHER]: 'Other',
};

// ============================================================================
// Validation Helpers
// ============================================================================

/**
 * Check if a value is a valid order status
 */
export const isValidOrderStatus = (value: string): value is OrderStatus => {
  return ORDER_STATUS_OPTIONS.includes(value as OrderStatus);
};

/**
 * Check if a value is a valid order source
 */
export const isValidOrderSource = (value: string): value is OrderSource => {
  return ORDER_SOURCE_OPTIONS.includes(value as OrderSource);
};

/**
 * Check if a value is a valid order category
 */
export const isValidOrderCategory = (value: string): value is OrderCategory => {
  return ORDER_CATEGORY_OPTIONS.includes(value as OrderCategory);
};

// ============================================================================
// Delivery Sticker Emoji Constants
// ============================================================================

/**
 * Universal delivery sticker emojis
 * These work across all business types and are used for key information
 */
export const DELIVERY_STICKER_EMOJIS = {
  // Core delivery information
  ADDRESS: '🌍',
  PHONE: '📱',
  PRICE: '💸',
  CUSTOMER: '👤',
  HOME: '🏠',
  
  // Status and actions
  COMPLETED: '✔️',
  COMPLETED_ALT: '✔',
  IN_PROGRESS: '🔄',
  TIME: '⏰',
  NOTES: '🖊️',
  NOTES_ALT: '🖊',
} as const;

/**
 * Category-specific emojis for industry customization
 * Businesses can use these in addition to universal emojis for better context
 */
export const CATEGORY_SPECIFIC_EMOJIS: Record<OrderCategory, string[]> = {
  [ORDER_CATEGORY.FOOD_RESTAURANT]: ['🍕', '🍔', '🍱', '🥗', '☕'],
  [ORDER_CATEGORY.PHARMACY_MEDICAL]: ['💊', '💉', '🏥', '⚕️'],
  [ORDER_CATEGORY.CLOTHING_FASHION]: ['👕', '👗', '👞', '👜', '🧥'],
  [ORDER_CATEGORY.ELECTRONICS]: ['📱', '💻', '⌚', '🎧', '📷'],
  [ORDER_CATEGORY.HOUSEHOLD_GOODS]: ['🏠', '🛋️', '🪑', '🛏️'],
  [ORDER_CATEGORY.CLEANING_SUPPLIES]: ['🧹', '🧽', '🧴', '🪣'],
  [ORDER_CATEGORY.KITCHEN_ITEMS]: ['🍳', '🔪', '🥄', '🍽️'],
  [ORDER_CATEGORY.BATHROOM_PRODUCTS]: ['🚿', '🧴', '🪥', '🧻'],
  [ORDER_CATEGORY.HOME_DECOR]: ['🖼️', '🕯️', '🪴', '💡'],
  [ORDER_CATEGORY.STORAGE_ORGANIZATION]: ['📦', '🗃️', '📁'],
  [ORDER_CATEGORY.TEXTILES]: ['🧵', '🪡', '🛏️'],
  [ORDER_CATEGORY.SMALL_APPLIANCES]: ['⚡', '🔌', '💡'],
  [ORDER_CATEGORY.TOOLS_HARDWARE]: ['🔧', '🔨', '⚙️', '🪛'],
  [ORDER_CATEGORY.RETAIL_GENERAL]: ['🛒', '🏪', '📦'],
  [ORDER_CATEGORY.OTHER]: [],
};

/**
 * Get all emojis that should be recognized in delivery stickers
 * Includes both universal emojis and category-specific ones
 */
export const getAllDeliveryEmojis = (category?: OrderCategory): string[] => {
  const universalEmojis = Object.values(DELIVERY_STICKER_EMOJIS);
  
  if (!category) {
    // Return all possible emojis if no category specified
    const allCategoryEmojis = Object.values(CATEGORY_SPECIFIC_EMOJIS).flat();
    return [...new Set([...universalEmojis, ...allCategoryEmojis])];
  }
  
  // Return universal + category-specific emojis
  const categoryEmojis = CATEGORY_SPECIFIC_EMOJIS[category] || [];
  return [...universalEmojis, ...categoryEmojis];
};
